import { useId } from "react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import type { RankBlazonInput, RankProgress } from "@/types/rank"

import { RankBlazon } from "./rank-blazon"
import { RankPatch } from "./rank-patch"

/**
 * OKS-512 — Real-photo avatar wrapped in a momentum ring. The second
 * foundational identity primitive (with `<RankPatch>`), consumed by the
 * profile (§11), leaderboard (§13), and feed (§10).
 *
 * # Two ring modes (UX Spec §1)
 *
 *   - `full` — a single slim tonal ring (decorative). Top-tier members
 *     (no "next" rank) use this — never an empty 0%/100% arc.
 *   - `progress` — a muted track + accent arc filled to
 *     `current / range` (the existing `RankProgress` wire shape, reused
 *     verbatim — no new XP math), with a small endpoint dot at the tip.
 *
 * The ring sits OUTSIDE a clean circular photo (`object-cover`). The arc
 * starts at 12 o'clock and sweeps clockwise. Fill is clamped 0..1 IN the
 * component (`range === 0` → 0, `current > range` → 1) so hosts can't
 * over/underflow it.
 *
 * # No purple placeholder (PRD AC)
 *
 * Fallback reuses shadcn `AvatarFallback` with the neutral `bg-muted`
 * token + initials — the old purple placeholder is gone.
 *
 * # Locked DOM contract (LQE assertions)
 *
 *   - root `<div data-testid="momentum-avatar" data-size>`
 *   - ring `<svg data-testid="momentum-ring" data-ring="full|progress">`
 *   - progress arc `<circle data-fraction>` (absent in `full` mode)
 *   - corner patch slot `data-testid="momentum-avatar-patch"`
 */
export interface MomentumRingAvatarProps {
  /** Photo URL; `null`/broken → initials fallback (Radix handles error). */
  src: string | null
  /** Display name — `alt` text + initials fallback. */
  name: string
  /** `full` = decorative ring; `progress` = XP-to-next arc. */
  ring: "full" | "progress"
  /** Required for `ring="progress"`; arc fill = `current / range`. */
  progress?: RankProgress | null
  /** Optional rank blazon/patch pinned at the bottom-right corner. */
  rankPatch?: RankBlazonInput
  /**
   * OKS-539 — Corner rendering. `shield` (default) keeps the generated
   * `<RankPatch>` — used by the leaderboard + feed, which stay on the
   * shield in v1 (PRD Q5, out of scope). `blazon` renders the frame-less
   * `<RankBlazon>` (uploaded image or position-tinted default) — the
   * creator identity head (profile, surface #2) opts into this.
   */
  cornerVariant?: "shield" | "blazon"
  /** `lg` = profile (~140px), `sm` = roster/feed row (~44px). */
  size?: "lg" | "sm"
  className?: string
}

// Normalized ring geometry — constant radius so geometry is independent
// of the rendered px size (LQE asserts `data-fraction`, not raw dasharray).
const RING_R = 46
const RING_CX = 50
const RING_CY = 50
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R

const SIZE_PX: Record<NonNullable<MomentumRingAvatarProps["size"]>, number> = {
  lg: 140,
  sm: 44,
}
const STROKE_W: Record<NonNullable<MomentumRingAvatarProps["size"]>, number> = {
  // `sm` ring is proportionally thicker so it stays legible at row size.
  lg: 5,
  sm: 7,
}
// Photo inset so the ring sits fully OUTSIDE the circular photo.
const PHOTO_SCALE = 0.84

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/** Stable, trimmed fixed-precision fraction for the `data-fraction` attr. */
function formatFraction(fraction: number): string {
  return parseFloat(fraction.toFixed(4)).toString()
}

export function MomentumRingAvatar({
  src,
  name,
  ring,
  progress,
  rankPatch,
  cornerVariant = "shield",
  size = "lg",
  className,
}: MomentumRingAvatarProps) {
  const { t } = useTranslation()
  const getInitials = useInitials()
  const gradientId = useId()

  const px = SIZE_PX[size]
  const strokeWidth = STROKE_W[size]
  const photoPx = Math.round(px * PHOTO_SCALE)
  const initials = name ? getInitials(name) : ""

  const isProgress = ring === "progress"
  const fraction =
    isProgress && progress && progress.range > 0
      ? clamp01(progress.current / progress.range)
      : isProgress
        ? 0
        : 1

  // Endpoint dot: angle measured from 12 o'clock, clockwise.
  const dotAngle = -Math.PI / 2 + 2 * Math.PI * fraction
  const dotX = RING_CX + RING_R * Math.cos(dotAngle)
  const dotY = RING_CY + RING_R * Math.sin(dotAngle)

  const progressAria = t("rank.avatar.progress_aria", {
    current: progress?.current ?? 0,
    range: progress?.range ?? 0,
  })

  return (
    <div
      data-testid="momentum-avatar"
      data-size={size}
      className={cn("relative inline-flex shrink-0", className)}
      style={{ width: px, height: px }}
    >
      <svg
        data-testid="momentum-ring"
        data-ring={ring}
        className="absolute inset-0"
        width={px}
        height={px}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...(isProgress
          ? { role: "img", "aria-label": progressAria }
          : { "aria-hidden": true })}
      >
        {ring === "full" ? (
          <g className="text-primary">
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="currentColor" stopOpacity="1" />
                <stop offset="1" stopColor="currentColor" stopOpacity="0.55" />
              </linearGradient>
            </defs>
            <circle
              cx={RING_CX}
              cy={RING_CY}
              r={RING_R}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth={strokeWidth}
            />
          </g>
        ) : (
          <>
            {/* Muted track */}
            <circle
              cx={RING_CX}
              cy={RING_CY}
              r={RING_R}
              fill="none"
              className="stroke-muted"
              strokeWidth={strokeWidth}
            />
            {/* Accent arc — 12 o'clock, clockwise */}
            <circle
              data-fraction={formatFraction(fraction)}
              cx={RING_CX}
              cy={RING_CY}
              r={RING_R}
              fill="none"
              className="stroke-primary"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={RING_CIRCUMFERENCE * (1 - fraction)}
              transform={`rotate(-90 ${RING_CX} ${RING_CY})`}
            />
            {/* Endpoint dot at the arc tip */}
            {fraction > 0 && (
              <circle
                data-testid="momentum-ring-dot"
                cx={dotX}
                cy={dotY}
                r={strokeWidth / 2 + 0.5}
                className="fill-primary"
              />
            )}
          </>
        )}
      </svg>

      {/* Photo — clean circle, image-fill, centered inside the ring */}
      <Avatar
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{ width: photoPx, height: photoPx }}
      >
        {src ? (
          <AvatarImage src={src} alt={t("rank.avatar.alt", { name })} />
        ) : null}
        <AvatarFallback className="bg-muted text-muted-foreground font-medium">
          {initials}
        </AvatarFallback>
      </Avatar>

      {/* Optional corner blazon — subtle halo so it reads on busy photos.
          `shield` (default) keeps the generated `<RankPatch>` (leaderboard
          + feed — PRD Q5). `blazon` renders the frame-less `<RankBlazon>`
          (OKS-539 profile identity head). Decorative either way — the
          surrounding UI (`<RankPill>`) names the rank. */}
      {rankPatch ? (
        <span
          data-testid="momentum-avatar-patch"
          className="absolute right-0 bottom-0 [filter:drop-shadow(0_0_1.5px_white)]"
        >
          {cornerVariant === "blazon" ? (
            <RankBlazon input={rankPatch} size="corner" decorative />
          ) : (
            <RankPatch
              tier={rankPatch.tier}
              tierCount={rankPatch.tierCount}
              name={rankPatch.name}
              size="sm"
            />
          )}
        </span>
      ) : null}
    </div>
  )
}
